import * as R from 'ramda';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose, withHandlers } from 'react-recompose';
import React, { useRef, useMemo, useState, useCallback } from 'react';
// features
import { AuthWrapper } from '../../../permission';
import PC from '../../../permission/role-permission';
import { makeSelectBranchesTree } from '../../../branch/selectors';
import { makeSelectAuthorities } from '../../../permission/selectors';
import { makeSelectPinnedBranches, makeSelectPinnedMenuItems } from '../../../profile/selectors';
// helpers/constants
import * as G from '../../../../helpers';
import { getCurrentMenuItem } from '../../../../helpers/menu';
// ui
import { Box } from '../../../../ui';
// feature navbar-menu
import * as H from '../../helpers';
import TopSection from './top-section';
import VersionRelease from './version-release';
import SidebarItemComponent from './sidebar-item';
import BranchesDropdown from './branches-dropdown';
import { HrDivider, LgMenuWrapper, SidebarSearchInput } from './ui';
import { toggleSubmenus, addPinnedItemToList } from '../../actions';
import { makeSelectSidebar, makeSelectSubmenusStatus } from '../../selectors';
//////////////////////////////////////////////////

const enhance = compose(
  withHandlers({
    handleClickPlusIcon: ({ addPinnedItemToList }: Object) =>
      (item: Object) => addPinnedItemToList(item.key),
  }),
);

const SidebarComponent = ({
  sidebar,
  styling,
  branches,
  iconsColor,
  branchTree,
  authorities,
  isSidebarOpen,
  subMenusStatus,
  pinnedBranches,
  toggleSubmenus,
  pinnedMenuItems,
  handleOpenEntTree,
  handleSwitchBranch,
  handleClickNavItem,
  handleClickPlusIcon,
}: Object) => {
  const sidebarRef = useRef(sidebar);

  const [typing, setTyping] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [submenusToggled, setSubmenusToggled] = useState(false);

  const stopTyping = useCallback(G.setDebounce(() => {
    setTyping(false);
    setSubmenusToggled(false);
  }, 200), []);

  const handleSetSearchText = useCallback((event: Event) => {
    const searchText = R.pathOr('', ['target', 'value'], event);

    setSearchText(searchText);

    if (R.lt(R.length(searchText), 2)) {
      return toggleSubmenus(R.map(() => false, subMenusStatus));
    }

    stopTyping();
    setTyping(true);
  }, []);

  const { filteredSideBar, subMenusExpanded } = useMemo(() => {
    if (typing) return sidebarRef.current;

    if (R.lt(R.length(searchText), 2)) {
      const data = {
        filteredSideBar: sidebar,
        subMenusExpanded: subMenusStatus,
      };

      sidebarRef.current = data;

      return data;
    }

    const criteria = R.toLower(searchText);

    const filtered = [];

    const subMenusExpanded = {...subMenusStatus};

    R.forEach(
      (menuItem: Object) => {
        const { key, title, submenu } = menuItem;

        const filteredSubmenu = R.filter(
          R.compose(
            R.includes(criteria),
            R.toLower,
            R.prop('title'),
          ),
          submenu,
        );

        const titleIncludes = R.includes(criteria, R.toLower(title));

        if (R.or(titleIncludes, G.isNotNilAndNotEmpty(filteredSubmenu))) {
          filtered.push({
            ...menuItem,
            submenu: filteredSubmenu,
          });

          if (R.not(submenusToggled)) {
            subMenusExpanded[key] = true;
          }
        }
      },
      sidebar,
    );

    const data = {
      subMenusExpanded,
      filteredSideBar: filtered,
    };

    sidebarRef.current = data;

    return data;
  }, [typing, sidebar, searchText, subMenusStatus, submenusToggled]);

  const handleToggleSubmenus = useCallback((statuses: Object) => {
    toggleSubmenus(statuses);

    setSubmenusToggled(true);
  }, []);

  const sidebarIconColor = R.pathOr(G.getTheme('colors.light.darkRed'), ['textColor'], styling);

  const branchDropdownProps = {
    branches,
    branchTree,
    pinnedBranches,
    handleOpenEntTree,
    handleSwitchBranch,
    iconsColor: sidebarIconColor,
  };

  const subItemsProps = {
    styling,
    authorities,
    pinnedMenuItems,
    handleClickNavItem,
    handleClickPlusIcon,
    iconsColor: sidebarIconColor,
    subMenusStatus: subMenusExpanded,
    currentItem: getCurrentMenuItem(),
    toggleSubmenus: handleToggleSubmenus,
  };

  const { glassEffect, backgroundColor } = styling;

  const bgColor = glassEffect ? H.hexToRgbA(backgroundColor, 0.8) : backgroundColor;

  return (
    <LgMenuWrapper
      bg={bgColor}
      glassEffect={glassEffect}
      isMenuOpen={isSidebarOpen}
    >
      <TopSection />
      <AuthWrapper has={[PC.BRANCH_WRITE]}>
        <BranchesDropdown {...branchDropdownProps} />
      </AuthWrapper>
      <HrDivider m={8} />
      <SidebarSearchInput
        mx={15}
        bg={bgColor}
        value={searchText}
        color={sidebarIconColor}
        onChange={handleSetSearchText}
        placeholder={G.getWindowLocale('titles:search', 'Search')}
      />
      <Box overflowY='auto' height='calc(100vh - 200px)'>
        {
          G.isNotEmpty(filteredSideBar)
          && filteredSideBar.map(
            (item: Object, i: number) => <SidebarItemComponent {...subItemsProps} key={i} item={item} />,
          )
        }
      </Box>
      <VersionRelease iconsColor={iconsColor} />
    </LgMenuWrapper>
  );
};

const mapStateToProps = (state: Object) => (createStructuredSelector({
  sidebar: makeSelectSidebar(state),
  authorities: makeSelectAuthorities(state),
  branchTree: makeSelectBranchesTree(state),
  subMenusStatus: makeSelectSubmenusStatus(state),
  pinnedBranches: makeSelectPinnedBranches(state),
  pinnedMenuItems: makeSelectPinnedMenuItems(state),
}));

export default connect(mapStateToProps, {
  toggleSubmenus,
  addPinnedItemToList,
})(enhance(SidebarComponent));
